/**
 * Custom hook for managing labels and lists data
 */
import { useState, useEffect } from "react"
import { LabelItem, LabelType, parseLabelsAndLists, updateLabelInQuestionnaire } from "utils/labelUtils"
import { EditorRef } from "types/questionnaire.types"
import { notify } from "shared/ui/Notifications/notify"
import { escapeHTML } from "utils/escapeHtml"
import { exportLabelsToExcel, importLabelsFromExcel } from "utils/labelExcelUtils"

interface UseLabelsManagerProps {
  editorRef: React.RefObject<EditorRef | null>
  setIsModalVisible: (visible: boolean) => void
  /** When provided, only labels of these types are surfaced/managed */
  allowedTypes?: LabelType[]
}

export const useLabelsManager = ({
  editorRef,
  setIsModalVisible,
  allowedTypes,
}: UseLabelsManagerProps) => {
  const [labels, setLabels] = useState<LabelItem[]>([])
  const [filteredLabels, setFilteredLabels] = useState<LabelItem[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  // Start in the loading state so the modal can paint a loader immediately while
  // the (potentially large) questionnaire is parsed off the initial render.
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [filterType, setFilterType] = useState<string>("ALL")

  /**
   * Fetches and parses labels from the editor content
   */
  const getLabels = (): void => {
    setIsLoading(true)
    try {
      if (!editorRef.current) {
        notify.error("Error", "Editor reference not available")
        return
      }

      const editorHTML = editorRef.current.getHTML()
      const text = editorHTML.replace(/<p>/g, "").replace(/<\/p>/g, "\n").trim()

      const unescapedText = text
        .replace(/&lt;&lt;/g, "<<")
        .replace(/&gt;&gt;/g, ">>")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")

      const parsedLabels = parseLabelsAndLists(unescapedText)
      const scopedLabels = allowedTypes
        ? parsedLabels.filter((label) => allowedTypes.includes(label.type))
        : parsedLabels
      setLabels(scopedLabels)
      setFilteredLabels(scopedLabels)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error Parsing Labels!", errorMessage)
    }
    setIsLoading(false)
  }

  /**
   * React effect to fetch labels on mount.
   * Deferred a tick so the modal paints (with a loader) before the heavy parse
   * and large-table render run — otherwise opening blocks for ~1s.
   */
  useEffect(() => {
    const timer = setTimeout(() => getLabels(), 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * React effect to filter labels when filter type changes
   */
  useEffect(() => {
    if (filterType === "ALL") {
      setFilteredLabels(labels)
    } else {
      const filtered = labels.filter(
        (label) => label.type === (filterType as LabelType)
      )
      setFilteredLabels(filtered)
    }
  }, [filterType, labels])

  /**
   * Handles changing the filter type
   */
  const handleFilterChange = (value: string): void => {
    setFilterType(value)
  }

  /**
   * Handles changing a label's content
   */
  const handleLabelChange = (id: string, newValue: string): void => {
    setLabels((prevLabels: LabelItem[]) => {
      return prevLabels.map((item: LabelItem) =>
        item.id === id ? { ...item, content: newValue } : item
      )
    })
    setHasUnsavedChanges(true)
  }

  /**
   * Updates the questionnaire with the modified labels
   */
  const handleUpdateQuestionnaire = async (
    validationErrors: Record<string, string>
  ): Promise<void> => {
    try {
      if (!editorRef.current) {
        notify.error("Error", "Editor reference not available")
        return
      }

      // Get current editor content
      const editorHTML = editorRef.current.getHTML()
      let text = editorHTML.replace(/<p>/g, "").replace(/<\/p>/g, "\n").trim()

      text = text
        .replace(/&lt;&lt;/g, "<<")
        .replace(/&gt;&gt;/g, ">>")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")

      // Parse original labels to compare
      const originalLabels = parseLabelsAndLists(text)

      // Check for validation errors
      const errorList = Object.entries(validationErrors).map(([id, error]) => {
        const label = labels.find(l => l.id === id)
        return `"${label?.name}": ${error}`
      })

      if (errorList.length > 0) {
        const errorMessage = errorList.join("\n")
        notify.error(
          "Invalid Question Label Format",
          `Please fix the following errors:\n\n${errorMessage}`
        )
        return
      }

      // Apply all label updates
      let updatedContent = text
      let changesMade = false

      for (const label of labels) {
        const originalLabel = originalLabels.find((l) => l.id === label.id)
        if (originalLabel && originalLabel.content !== label.content) {
          updatedContent = updateLabelInQuestionnaire(updatedContent, label)
          changesMade = true
        }
      }

      // Check if anything actually changed
      if (!changesMade) {
        notify.success("No Changes", "No labels or lists were modified.")
        return
      }

      // Convert back to HTML and update editor
      const updatedHTML = updatedContent
        .split("\n")
        .map((s: string) =>
          s.trim() === "" ? "<p></p>" : "<p>" + escapeHTML(s) + "</p>"
        )
        .join("")

      // Update the editor content using the setHTML method
      editorRef.current.setHTML(updatedHTML)

      notify.success(
        "Labels Updated!",
        "All labels and lists have been updated. Don't forget to save the questionnaire!"
      )

      setHasUnsavedChanges(false)

      // Refresh the labels to show updated state
      setTimeout(() => {
        getLabels()
      }, 100)

      // Close modal
      setIsModalVisible(false)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error Updating Questionnaire!", errorMessage)
    }
  }

  /**
   * Handles exporting labels to Excel
   */
  const handleExportToExcel = async (): Promise<void> => {
    try {
      if (!labels || labels.length === 0) {
        notify.error("No Labels", "There are no labels to export.")
        return
      }

      const filename = `questionnaire-labels-${new Date().toISOString().split("T")[0]}`
      const success = await exportLabelsToExcel(labels, filename)

      if (success) {
        notify.success(
          "Export Successful!",
          `Exported ${labels.length} label${labels.length === 1 ? "" : "s"} to Excel.`
        )
      } else {
        notify.error("Export Failed", "Failed to export labels to Excel.")
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Export Error!", errorMessage)
    }
  }

  /**
   * Handles importing labels from Excel
   */
  const handleImportFromExcel = async (file: File): Promise<void> => {
    try {
      setIsLoading(true)

      const importResult = await importLabelsFromExcel(file, labels)

      // Check for validation errors
      if (!importResult.validationResult.isValid) {
        const errorMessage = importResult.validationResult.errors.join("\n")
        notify.error(
          "Import Validation Failed",
          `Please fix the following errors in your Excel file:\n\n${errorMessage}`
        )
        setIsLoading(false)
        return
      }

      // Show warnings if any
      if (importResult.validationResult.warnings.length > 0) {
        const warningMessage = importResult.validationResult.warnings.join("\n")
        // Show warnings as success notifications since there's no WarningNotification
        notify.success("Import Warnings", warningMessage)
      }

      // Check if any changes were found
      if (importResult.labels.length === 0) {
        notify.success(
          "No Changes",
          "No changes detected in the imported file."
        )
        setIsLoading(false)
        return
      }

      // Apply the imported changes
      setLabels((prevLabels: LabelItem[]) => {
        return prevLabels.map((item: LabelItem) => {
          const importedLabel = importResult.labels.find((l) => l.id === item.id)
          if (importedLabel && importedLabel.content) {
            return { ...item, content: importedLabel.content }
          }
          return item
        })
      })

      setHasUnsavedChanges(true)

      notify.success(
        "Import Successful!",
        `Imported ${importResult.labels.length} label change${importResult.labels.length === 1 ? "" : "s"}. Don't forget to click "Update Questionnaire" to apply changes.`
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Import Error!", errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    labels,
    filteredLabels,
    hasUnsavedChanges,
    isLoading,
    filterType,
    handleFilterChange,
    handleLabelChange,
    handleUpdateQuestionnaire,
    getLabels,
    handleExportToExcel,
    handleImportFromExcel,
  }
}
